/*
  AdminRequest.js - AdminRequest Networking File

  Contains networking functionality for Administration Components
*/

// MODULES
import RequestRabbit from './RequestRabbit';
import { Buffer } from 'buffer';

// CONFIGURATION
import config from '../Config';

const useAdminRequest = () => {
  const updateProvider = async (providerId, updateData, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/provider/${providerId}`;
      const body = JSON.stringify(updateData);
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      const options = {
        method: 'POST',
        body: body,
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const getProviderExtra = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/provider/extra`;
      const options = {
        method: 'GET',
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const getAllCustomers = async (providerFilter, errorHandler) => {
    try {
      const params = new URLSearchParams();
      if (providerFilter) params.append('provider_id', providerFilter);

      const url = `${
        config.baseServerURL
      }/admin/customer/?${params.toString()}`;

      const options = {
        method: 'GET',
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const getSingleCustomer = async (customerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}`;
      const options = {
        method: 'GET',
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const updateCustomer = async (customerId, updateData, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}`;
      const body = JSON.stringify(updateData);
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      const options = {
        method: 'POST',
        body: body,
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const getTwilioNumbers = async (
    selectedCountry,
    region,
    postalCode,
    areaCode,
    contains,
    errorHandler
  ) => {
    try {
      if (!selectedCountry) errorHandler('Selected country required.');
      let url = `${config.baseServerURL}/twilio/number?countryCode=${selectedCountry}`;
      if (region) url = url + `&regionCode=${region}`;
      if (postalCode) url = url + `&postalCode=${postalCode}`;
      if (areaCode) url = url + `&areaCode=${areaCode}`;
      if (contains) url = url + `&contains=${contains}`;

      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const saveCreditCardToken = async (
    paymentProvider,
    ccToken,
    errorHandler
  ) => {
    try {
      let url = `${config.baseServerURL}/payments/card`;
      let body = {
        paymentProvider: paymentProvider,
      };
      if (paymentProvider.toLowerCase() === 'securepay') body.token = ccToken;
      else if (paymentProvider.toLowerCase() === 'payeezy') body.cc = ccToken;
      body = JSON.stringify(body);
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      const options = {
        method: 'POST',
        body: body,
        headers: headers,
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const updateActingProvider = async (providerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/session/acting-provider`;
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      let body = {
        actingProviderId: providerId,
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const updateActingCustomer = async (customerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/session/acting-customer`;
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      let body = {
        actingCustomerId: customerId,
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const getCustomerComments = async (customerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/comments`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const editCustomerComments = async (
    customerId,
    commentId,
    comment,
    errorHandler
  ) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/comments/${commentId}`;
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      let body = {
        customer_comments: comment,
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: headers,
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const deleteCustomerComments = async (
    customerId,
    commentId,
    errorHandler
  ) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/comments/${commentId}`;
      const options = {
        method: 'DELETE',
      };

      await RequestRabbit.request(url, options, errorHandler);
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const addCustomerComments = async (customerId, comment, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/comments`;
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      let body = {
        customer_comments: comment,
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: headers,
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const addCustomerAttachments = async (
    customerId,
    type,
    file,
    errorHandler,
    typeTag
  ) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/attachments`;

      let formData = new FormData();
      formData.set('upload_type', type);
      formData.set('file', file);

      if (typeTag) formData.set('type_tag', typeTag);

      const options = {
        method: 'POST',
        body: formData,
      };
      let response = await RequestRabbit.request(url, options, errorHandler);

      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
      }

      return responseJSON;
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const getCustomerAttachments = async (customerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/attachments`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      console.log('customer attachments endpoint:', responseJSON);
      return responseJSON;
    } catch (e) {
      errorHandler(e);
    }
  };

  const deleteCustomerAttachment = async (
    customerId,
    attachmentId,
    errorHandler
  ) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/attachments/${attachmentId}`;
      const options = {
        method: 'DELETE',
      };
      await RequestRabbit.request(url, options, errorHandler);
    } catch (e) {
      errorHandler(e.message);
    }
  };

  const downloadCustomerAttachment = async (
    customerId,
    attachmentId,
    attachmentName,
    errorHandler
  ) => {
    try {
      const url = `${config.baseServerURL}/admin/customer/${customerId}/attachments/${attachmentId}`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);

      if (response?.status > 399 && response?.status < 600) {
        let error = await response?.json();
        if (error.err) errorHandler(error.err);
        else errorHandler('Got file download error');
        return;
      }

      const blob = await response?.blob();

      if (navigator.userAgent.includes('Edge')) {
        navigator.msSaveBlob(blob, attachmentName);
      }

      let downloadUrl = URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = downloadUrl;
      tempLink.setAttribute('download', attachmentName);

      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);

      URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      errorHandler(e);
    }
  };

  const getCardDetails = async (customerId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/billing/card/get/admin`;
      let data = { customerId: customerId };
      const body = JSON.stringify(data);
      const headers = new Headers({
        'Content-Type': 'application/json',
      });
      const options = {
        method: 'POST',
        body: body,
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();

      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getPrimaryQueueStats = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/stats/primaryQueue`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getTotalStatsPerMuxId = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/stats/total`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getTotalTwilioStatsPerMuxId = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/twiliostats/total`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getMuxIdDrilldown = async (muxId, isTwilio, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/stats/${muxId}`;
      const headers = { 'Content-Type': 'application/json' };
      const body = {
        isTwilio: isTwilio,
      };
      const options = {
        method: 'POST',
        body: JSON.stringify(body),
        headers: headers,
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getDaemonConfigByRouteName = async (routeName, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/daemonconfig/${routeName}`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const getRecentMessagesByRouteId = async (routeId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/recentmessages/${routeId}`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const sendMuxShutdown = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/muxshutdown`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const sendApiShutdown = async (errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/apishutdown`;
      const options = {
        method: 'GET',
      };
      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        errorHandler(responseJSON.error);
        return [];
      }
      return responseJSON;
    } catch (e) {
      return errorHandler(e);
    }
  };

  const debugEmail = async (type, email, errorHandler) => {
    const url = `${config.baseServerURL}/admin/debug/email`;
    const options = {
      method: 'POST',
      body: JSON.stringify({
        type,
        email,
      }),
      headers: { 'Content-Type': 'application/json' },
    };

    try {
      const response = await RequestRabbit.request(url, options, errorHandler);

      if (response?.status > 399 && response?.status < 600) {
        return response?.statusText;
      }

      return true;
    } catch (e) {
      errorHandler(e);
      return e;
    }
  };

  const getTrace = async (filter, errorHandler) => {
    try {
      const params = new URLSearchParams(filter);
      const url = `${
        config.baseServerURL
      }/admin/systemmonitor/trace?${params.toString()}`;
      const options = {
        method: 'GET',
      };

      let response = await RequestRabbit.request(url, options, errorHandler);
      let responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        if (errorHandler) {
          errorHandler(responseJSON.error);
        } else {
          console.error(responseJSON.error);
        }

        return [];
      }
      return responseJSON;
    } catch (e) {
      if (errorHandler) {
        return errorHandler(e);
      }

      console.error(e);
      return [];
    }
  };

  const getTraceMessages = async (customerId, date, to, page, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/trace/${customerId}?date=${date}&to=${to}&page=${page}`;
      const options = {
        method: 'GET',
      };

      const response = await RequestRabbit.request(url, options, errorHandler);
      const responseJSON = await response?.json();
      if (response?.status > 399 && response?.status < 600) {
        if (errorHandler) {
          errorHandler(responseJSON.error);
        } else {
          console.error(responseJSON.error);
        }

        return [];
      }
      return responseJSON;
    } catch (e) {
      if (errorHandler) {
        return errorHandler(e);
      }

      console.error(e);
      return [];
    }
  };

  const getTraceEvents = async (smsId, emailId, errorHandler) => {
    try {
      const url = `${config.baseServerURL}/admin/systemmonitor/trace/${smsId}/events?email_id=${emailId}`;
      const options = {
        method: 'GET',
      };
      const response = await RequestRabbit.request(url, options, errorHandler);
      const responseJSON = await response?.json();
      return responseJSON;
    } catch (e) {
      if (errorHandler) {
        return errorHandler(e);
      }

      console.error(e);
      return [];
    }
  };

  const getAuthGroups = async (date, page, pageSize, errorHandler) => {
    try {
      const params = new URLSearchParams({ date, page, pageSize });
      const url = `${
        config.baseServerURL
      }/admin/systemmonitor/trace/auth?${params.toString()}`;
      const options = {
        method: 'GET',
      };
      const response = await RequestRabbit.request(url, options, errorHandler);
      const responseJSON = await response?.json();
      return responseJSON;
    } catch (e) {
      if (errorHandler) {
        return errorHandler(e);
      }

      console.error(e);
      return [];
    }
  };

  const getAuthLogs = async (email, date, page, pageSize, errorHandler) => {
    try {
      const params = new URLSearchParams({ email, date, page, pageSize });
      const url = `${
        config.baseServerURL
      }/admin/systemmonitor/trace/auth?${params.toString()}`;
      const options = {
        method: 'GET',
      };
      const response = await RequestRabbit.request(url, options, errorHandler);
      const responseJSON = await response?.json();
      return responseJSON;
    } catch (e) {
      if (errorHandler) {
        return errorHandler(e);
      }

      console.error(e);
      return [];
    }
  };

  const getPdf = async (type, email, customerId, errorHandler) => {
    const url = `${config.baseServerURL}/admin/debug/pdf`;
    const options = {
      method: 'POST',
      body: JSON.stringify({ type, email, customerId }),
      headers: { 'Content-Type': 'application/json' },
    };

    try {
      const response = await RequestRabbit.request(url, options, errorHandler);
      if (!response.ok) {
        errorHandler(response.statusText);
        return false;
      }

      if (email) {
        return true;
      }

      const base64 = await response.text();
      const binary = Buffer.from(base64, 'base64');

      return new Blob([binary], { type: 'application/pdf' });
    } catch (e) {
      errorHandler(e.message);
      return e;
    }
  };

  // Export functions as a module
  return {
    updateProvider,
    getProviderExtra,
    getAllCustomers,
    getSingleCustomer,
    updateCustomer,
    getTwilioNumbers,
    saveCreditCardToken,
    updateActingProvider,
    updateActingCustomer,
    getCustomerComments,
    editCustomerComments,
    deleteCustomerComments,
    addCustomerComments,
    addCustomerAttachments,
    getCustomerAttachments,
    deleteCustomerAttachment,
    downloadCustomerAttachment,
    getCardDetails,
    getPrimaryQueueStats,
    getTotalStatsPerMuxId,
    getTotalTwilioStatsPerMuxId,
    getMuxIdDrilldown,
    getDaemonConfigByRouteName,
    getRecentMessagesByRouteId,
    sendMuxShutdown,
    sendApiShutdown,
    debugEmail,
    getTrace,
    getTraceMessages,
    getTraceEvents,
    getAuthGroups,
    getAuthLogs,
    getPdf,
  };
};
export default useAdminRequest;
